/**
 * Screen 6 — "Is it live". The verification tiers as plan.md numbers them:
 *   0  passive impression beacon — polled here; the source of truth
 *   1  server fetch-and-scan of a page on a registered origin — install health
 *      only: a pass says the widget is on the page, never that the partner
 *      owns the website (#854: a page can reflect someone else's snippet)
 *   2  headless render — the verify-install container (T034) opens the page
 *      in headless Chromium and checks the widget mounted with this key.
 *      Install health only, like tier 1 (#854). The row is `pending` until the
 *      render finishes, and the list polls while a fresh one is.
 *   3  `.well-known/finderly-site-verification.txt` containing the key (what
 *      shipped instead of the guided live test; see plan.md §4 notes) — the
 *      ONLY check that verifies ownership (sets `verified_at`)
 *   4  manual — a console one-liner, documentation only
 * Never blocks: "I'll do this later" continues.
 *
 * "Installed" is setup/status.ts `installState`, shared with the rail and the
 * dashboard: an impression (Live) or a passing tier 1/2 check. Ownership
 * alone does not make the step done.
 *
 * Each tier card shows its latest result for the chosen website and path
 * (from the recent-checks list): passed → a check mark and "Re-check", failed
 * → the reason and "Try again", never run → a short description. Every tier
 * stays runnable after it passes; re-confirming after a redeploy is the point.
 */
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useSession } from '../auth/session';
import type { Verification, VerificationResult } from '../api/types';
import { WELL_KNOWN_FILE, wellKnownPath } from '../install/snippets';
import { sameOrigin, samePage, useCheckTarget } from '../install/target';
import { OnboardingPage } from '../setup/OnboardingPage';
import { installState } from '../setup/status';
import { useSites } from '../state/sites';
import { useLastSeen } from '../state/lastSeen';
import { CodeBlock, CopyButton, Empty, ErrorNotice, Field, Loading, Time, formatDateTime } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

const RESULT_COPY: Record<VerificationResult, { tone: string; label: string }> = {
  found: { tone: 'ok', label: 'Found' },
  not_in_html: { tone: 'bad', label: 'Not found' },
  file_missing: { tone: 'bad', label: 'File not found' },
  csp: { tone: 'bad', label: 'Blocked by CSP' },
  blocked: { tone: 'bad', label: 'Not allowed' },
  timeout: { tone: 'warn', label: 'Timed out' },
  error: { tone: 'bad', label: 'Error' },
  pending: { tone: 'warn', label: 'Queued' },
};

/** How long a pending tier-2 row keeps the list polling. */
const PENDING_POLL_WINDOW_MS = 5 * 60_000;

/** A specific cause for each failure (plan.md: "specific causes on failure"). */
export function explainVerification(v: Pick<Verification, 'tier' | 'result' | 'details'>): string {
  const reason = typeof v.details?.reason === 'string' ? v.details.reason : '';
  const status = typeof v.details?.status === 'number' ? v.details.status : null;
  switch (v.result) {
    case 'found':
      return v.tier === 3
        ? 'Verified with the file: it is there and contains your key, so this website is yours.'
        : 'Widget found on your page with your key. This checks the install, not ownership; “Prove you own the site” does that.';
    case 'pending':
      return 'Opening your page in a browser. This usually takes under a minute; the list updates by itself.';
    case 'csp':
      return 'The page’s Content-Security-Policy does not allow cdn.finderlyfix.com in script-src. Add the CSP sources from Install.';
    case 'file_missing':
    case 'not_in_html':
      if (reason === 'well_known_missing') return 'The ownership file was not found at that address.';
      if (reason === 'site_key_missing') {
        return v.tier === 3
          ? 'The ownership file exists but does not contain this key.'
          : 'The widget is on the page, but with a different site key. Copy the snippet from Install again.';
      }
      return v.tier === 2
        ? 'The page loaded in a browser, but no widget with this key appeared on it.'
        : 'The page loaded but has no widget with this key. If the page is built with JavaScript, try “Check pages built with JavaScript”.';
    case 'blocked':
      if (reason === 'redirected_off_site') return 'The page redirected to a website that is not registered for this site.';
      return typeof v.details?.message === 'string' ? v.details.message : 'That address is not one we are allowed to fetch.';
    case 'timeout':
      return 'The page took too long to answer.';
    case 'error': {
      if (reason === 'http_status' && status) return `The page answered HTTP ${status}.`;
      const loaderStatus = typeof v.details?.loader_status === 'number' ? v.details.loader_status : null;
      if (reason === 'loader_not_loaded') {
        return `Your widget tag is on the page, but the widget script did not load${loaderStatus ? ` (HTTP ${loaderStatus})` : ''}. Check the script line from Install.`;
      }
      if (reason === 'widget_not_mounted') return 'The widget script loaded, but the widget with your key did not start on the page.';
      if (reason === 'expired') return 'This render waited too long in the queue and did not run. Run it again.';
      if (reason === 'renderer_failed') return 'We could not run this check. Run it again, or use “Check the page” or “Prove you own the site”.';
      if (reason === 'navigation_failed') return 'The page could not be opened in a browser.';
      return 'The page could not be fetched.';
    }
    default:
      return '';
  }
}

type Tier = 1 | 2 | 3;
type CardState = 'passed' | 'failed' | 'pending' | 'idle';

/** How a recorded check reads on its tier card. */
export function cardState(v: Pick<Verification, 'result' | 'created_at'> | undefined): CardState {
  if (!v) return 'idle';
  if (v.result === 'found') return 'passed';
  if (v.result === 'pending') {
    const fresh = v.created_at !== null && Date.now() - Date.parse(v.created_at) < PENDING_POLL_WINDOW_MS;
    return fresh ? 'pending' : 'failed';
  }
  return 'failed';
}

/** The newest check of `tier` for this page (tiers 1–2) or website (tier 3); `items` is newest first. */
export function latestCheck(items: Verification[], tier: Tier, origin: string, pageUrl: string): Verification | undefined {
  return items.find((v) => v.tier === tier && (tier === 3 ? sameOrigin(v.url, origin) : samePage(v.url, pageUrl)));
}

/**
 * Partner-facing names. Tier numbers are internal (plan.md) and stay out of
 * the UI; the Recent checks table uses these names too.
 */
export const TIERS: Record<Tier, { title: string; short: string; long: string; action: string }> = {
  1: {
    title: 'Check the page',
    short: 'We fetch the page and look for the widget with your key.',
    long: 'We fetch the page and look for the widget and your key. Checks the install; does not verify ownership.',
    action: 'Check page',
  },
  2: {
    title: 'Check pages built with JavaScript',
    short: 'For pages built by JavaScript or a tag manager.',
    long: 'We open the page in a real browser and check the widget starts with your key. For pages built by JavaScript or a tag manager. Checks the install; does not verify ownership.',
    action: 'Check in a browser',
  },
  3: {
    title: 'Prove you own the site',
    short: 'Proves the website is yours.',
    long: 'The only check that proves the website is yours: we read the file below on your website and look for your key.',
    action: 'Check file',
  },
};

function StatusIcon({ tone }: { tone: 'ok' | 'bad' | 'pending' | 'neutral' }) {
  return <span className={`status-icon ${tone}`} aria-hidden="true" />;
}

function TierCard({
  tier,
  check,
  running,
  disabled,
  onRun,
  extra,
}: {
  tier: Tier;
  check: Verification | undefined;
  running: boolean;
  disabled: boolean;
  onRun: () => void;
  extra?: ReactNode;
}) {
  const copy = TIERS[tier];
  const state = running ? 'pending' : cardState(check);
  const titleId = `tier-${tier}-title`;
  return (
    <article className={`tier-card ${state}`} aria-labelledby={titleId}>
      <h3 id={titleId} className="tier-title">
        {copy.title}
      </h3>
      {state === 'passed' && check ? (
        <>
          <p className="status-line ok">
            <StatusIcon tone="ok" />
            <span>
              Passed <Time iso={check.created_at} />
            </span>
          </p>
          <details className="help">
            <summary>What this checks</summary>
            <p>{copy.long}</p>
            {extra}
          </details>
        </>
      ) : state === 'failed' && check ? (
        <>
          <p className="status-line bad">
            <StatusIcon tone="bad" />
            <span>
              <strong>{RESULT_COPY[check.result]?.label ?? 'Failed'}</strong> <Time iso={check.created_at} />
            </span>
          </p>
          <p className="small">{explainVerification(check)}</p>
          {extra}
        </>
      ) : state === 'pending' ? (
        <>
          <p className="status-line pending">
            <StatusIcon tone="pending" />
            <span>{running ? 'Checking…' : 'Opening your page in a browser…'}</span>
          </p>
          {extra}
        </>
      ) : (
        <>
          <p className="small muted">{copy.short}</p>
          {extra}
        </>
      )}
      <div className="tier-action">
        <button
          type="button"
          className={state === 'failed' ? 'btn btn-primary btn-sm' : state === 'idle' && tier === 1 ? 'btn btn-primary btn-sm' : 'btn btn-sm'}
          disabled={disabled || state === 'pending'}
          onClick={onRun}
          aria-describedby={titleId}
        >
          {state === 'passed' ? 'Re-check' : state === 'failed' ? 'Try again' : copy.action}
        </button>
      </div>
    </article>
  );
}

/** Tier 3's file: path only (breaks at slashes, never mid-word) with copy buttons. */
function OwnershipFile({ origin, siteKey }: { origin: string; siteKey: string }) {
  const parts = WELL_KNOWN_FILE.split('/').filter(Boolean);
  return (
    <div className="stack-sm" style={{ gap: 6 }}>
      <span className="small muted">Serve this file containing your live key:</span>
      <code className="path-code" title={wellKnownPath(origin || 'https://your-site')}>
        {parts.map((p, i) => (
          <Fragment key={p}>
            {i > 0 ? <wbr /> : null}
            <span>/{p}</span>
          </Fragment>
        ))}
      </code>
      <div className="row" style={{ gap: 6 }}>
        <CopyButton text={wellKnownPath(origin || 'https://your-site')} label="Copy URL" className="btn btn-sm btn-ghost" />
        <CopyButton text={siteKey} label="Copy key" className="btn btn-sm btn-ghost" />
      </div>
    </div>
  );
}

export function VerifyPanel({ onStatus }: { onStatus?: (verified: boolean) => void } = {}) {
  const { api } = useSession();
  const { current, reload: reloadSites } = useSites();
  const key = current?.live.site_key ?? '';
  const origins = useQuery(() => api.listOrigins(key), [key], { enabled: Boolean(key) });
  // Poll while a fresh tier-2 render is still pending (it finishes in the
  // background). Only fresh ones: a row that never ran would poll forever.
  const [watchPending, setWatchPending] = useState(false);
  const history = useQuery(() => api.listVerifications(key), [key], {
    enabled: Boolean(key),
    pollMs: watchPending ? 4_000 : undefined,
  });
  const hasFreshPending = Boolean(
    history.data?.items.some((v) => v.result === 'pending' && v.created_at !== null && Date.now() - Date.parse(v.created_at) < PENDING_POLL_WINDOW_MS),
  );
  useEffect(() => setWatchPending(hasFreshPending), [hasFreshPending]);
  // Tier 0: poll the site list for a first accepted impression.
  useQuery(() => reloadSites(), [key], { enabled: Boolean(key) && !current?.live.last_impression_at, pollMs: 10_000 });
  const list = (origins.data?.items ?? []).filter((o) => !o.blocked_at);
  const target = useCheckTarget(key, list.map((o) => o.origin));
  const [running, setRunning] = useState<Tier | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const run = useMutation(async (tier: Tier) => {
    const url = tier === 3 ? wellKnownPath(target.origin) : target.url;
    setRunning(tier);
    setAnnouncement('');
    try {
      const res = await api.verify(key, url, tier);
      await history.reload();
      const label = RESULT_COPY[res.result]?.label ?? res.result;
      setAnnouncement(`${TIERS[tier].title}: ${res.result === 'found' ? 'passed' : label}.`);
      return res;
    } finally {
      setRunning(null);
    }
  });

  const items = history.data?.items ?? [];
  const checks = {
    1: latestCheck(items, 1, target.origin, target.url),
    2: latestCheck(items, 2, target.origin, target.url),
    3: latestCheck(items, 3, target.origin, target.url),
  } as const;
  const lastSeen = useLastSeen();
  const impression = lastSeen.site(current?.live.site_key, current?.live.last_impression_at);
  const installPass = ([1, 2] as const)
    .map((t) => ({ tier: t, check: checks[t] }))
    .filter((x) => cardState(x.check) === 'passed')
    .sort((a, b) => Date.parse(b.check!.created_at ?? '') - Date.parse(a.check!.created_at ?? ''))[0];
  const chosenOrigin = list.find((o) => o.origin === target.origin);
  // The shared definition (setup/status.ts): live impressions or a passing
  // page check mean installed; ownership is a separate, optional line.
  const state = installState({
    last_impression_at: impression,
    install_check_passed: Boolean(installPass),
    ownership_verified: Boolean(chosenOrigin?.verified_at) || cardState(checks[3]) === 'passed',
  });
  const owned = state.owned;
  const verified = state.installed;
  useEffect(() => onStatus?.(verified), [onStatus, verified]);

  if (!current) return <Empty title="No site yet" />;

  return (
    <div className="stack-lg">
      <section className="panel panel-pad stack-sm" aria-labelledby="verify-status">
        <h2 id="verify-status" className="visually-hidden">
          Status
        </h2>
        <ul className="status-list" aria-live="polite">
          <li>
            {impression ? (
              <>
                <StatusIcon tone="ok" />
                <span>
                  <strong>Live</strong> · last seen <Time iso={impression} />
                </span>
              </>
            ) : (
              <>
                <StatusIcon tone="pending" />
                <span>
                  <strong>Not live yet</strong> · waiting for the widget to load on your site…
                </span>
              </>
            )}
          </li>
          {list.length ? (
            <>
              <li>
                <StatusIcon tone={state.installed ? 'ok' : 'neutral'} />
                {installPass ? (
                  <span>
                    <strong>Installed</strong> · “{TIERS[installPass.tier].title}” passed <Time iso={installPass.check!.created_at} />
                  </span>
                ) : state.live ? (
                  <span>
                    <strong>Installed</strong> · the widget loaded on your site <Time iso={impression} />
                  </span>
                ) : (
                  <span>
                    <strong>Install not confirmed</strong> · run a check below
                  </span>
                )}
              </li>
              <li>
                <StatusIcon tone={owned ? 'ok' : 'neutral'} />
                {owned ? (
                  <span>
                    <strong>Ownership verified</strong>
                  </span>
                ) : (
                  <span>
                    <strong>Ownership not verified</strong> · optional: “Prove you own the site” below
                  </span>
                )}
              </li>
            </>
          ) : null}
        </ul>
        <p className="small muted">Live status refreshes every 10 seconds while this page is open. Ad blockers can hide it; the checks below can’t be hidden.</p>
      </section>

      {list.length === 0 ? (
        <div className="notice">
          Checks run against websites where your key has been used. Open the page with the snippet once and it will
          appear here within a minute; nothing is blocked in the meantime.
        </div>
      ) : (
        <section className="panel" aria-labelledby="verify-run">
          <div className="panel-head">
            <h2 id="verify-run">Run a check</h2>
          </div>
          <div className="panel-body stack">
            <div className="grid-2">
              <Field label="Website">
                {(p) => (
                  <select {...p} className="input" value={target.origin} onChange={(e) => target.setOrigin(e.target.value)}>
                    {list.map((o) => (
                      <option key={o.id} value={o.origin}>
                        {o.origin}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="Page path" hint="Where you pasted the snippet.">
                {(p) => <input {...p} className="input input-mono" value={target.path} onChange={(e) => target.setPath(e.target.value)} />}
              </Field>
            </div>
            <div className="tier-grid">
              {([1, 2, 3] as const).map((tier) => (
                <TierCard
                  key={tier}
                  tier={tier}
                  check={checks[tier]}
                  running={running === tier}
                  disabled={run.pending}
                  onRun={() => void run.run(tier)}
                  extra={tier === 3 ? <OwnershipFile origin={target.origin} siteKey={key} /> : undefined}
                />
              ))}
            </div>
            <p className="visually-hidden" role="status" aria-live="polite">
              {announcement}
            </p>
            <ErrorNotice error={run.error} />
          </div>
        </section>
      )}

      <section className="panel" aria-labelledby="verify-history">
        <div className="panel-head">
          <h2 id="verify-history">Recent checks</h2>
        </div>
        {history.loading ? (
          <Loading />
        ) : history.error ? (
          <div className="panel-body">
            <ErrorNotice error={history.error} />
          </div>
        ) : items.length ? (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Check</th>
                  <th scope="col">Result</th>
                  <th scope="col">What it means</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id}>
                    <td className="num nowrap" title={v.url}>
                      {formatDateTime(v.created_at)}
                    </td>
                    <td>{TIERS[v.tier as Tier]?.title ?? 'Manual check'}</td>
                    <td>
                      <span className={`tag ${RESULT_COPY[v.result]?.tone ?? ''}`}>{RESULT_COPY[v.result]?.label ?? v.result}</span>
                    </td>
                    <td className="small">{explainVerification(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty title="No checks yet" />
        )}
      </section>

      <details className="panel panel-pad">
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>For developers</summary>
        <div className="stack" style={{ marginTop: 12 }}>
          <h3 className="h-sub">Check it yourself in the browser console</h3>
          <p className="small muted">Open your page, then paste this into the browser’s developer console. It prints the key the widget found and whether it rendered.</p>
          <CodeBlock
            caption="Console check"
            code={`(() => { const w = document.querySelector('finderly-diagnosis-widget'); console.log(w ? { siteKey: w.getAttribute('site-key'), rendered: Boolean(w.shadowRoot && w.shadowRoot.childElementCount) } : 'No <finderly-diagnosis-widget> on this page'); })();`}
          />
        </div>
      </details>
    </div>
  );
}

export function Verify() {
  const { loading } = useSites();
  const [verified, setVerified] = useState(false);
  return (
    <OnboardingPage
      slug="verify"
      lede="We check automatically. Nothing here blocks you; come back any time."
      continueLabel={verified ? 'Verified ✓ — Continue' : 'Continue (I’ll check later)'}
    >
      {loading ? <Loading /> : <VerifyPanel onStatus={setVerified} />}
    </OnboardingPage>
  );
}
